import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  InputAdornment,
  Snackbar,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate } from "react-router-dom";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

const ActiveUser = () => {
  const selectedManufacturer = JSON.parse(localStorage.getItem("selectedMfr"));
  const mfr_id = selectedManufacturer?.id;

  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterTime, setFilterTime] = useState("all");
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [activeUsers, setActiveUsers] = useState([]);
  const [activeLinks, setActiveLinks] = useState([]);
  const [selectedLinkId, setSelectedLinkId] = useState(""); // <-- allow empty to show all users
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [noUsers, setNoUsers] = useState(false);
  const [itemsLocked, setItemsLocked] = useState({});

  useEffect(() => {
    const getAllActiveLinks = async () => {
      try {
        const activeLinksFetcher = await axios.get(
          `${API_BASE_URL}/api/auth/links/${mfr_id}`
        );

        if (activeLinksFetcher.data?.statusCode !== 200)
          throw activeLinksFetcher.data;

        const allActiveLinks = activeLinksFetcher.data?.data || [];
        setActiveLinks(allActiveLinks);

        // DO NOT SELECT ANY LINK BY DEFAULT → then shows ALL users
        // setSelectedLinkId(allActiveLinks[0].linkId);
      } catch (error) {
        console.error(error);
      }
    };

    getAllActiveLinks();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingUsers(true);
        setNoUsers(false);

        const res = await axios.get(
          `${API_BASE_URL}/api/auth/session/active-users`
        );
        const allActiveUsers = res.data?.data || [];

        if (!res.data?.success) throw res.data;

        // ----------------------------------------
        // 🔥 CASE 1: SELECTED = "" → SHOW ALL USERS (GROUPED)
        // ----------------------------------------
        if (!selectedLinkId) {
          // const merged = allActiveUsers.flatMap((link) => link.users || []);
          const merged = allActiveUsers.flatMap((link) =>
            (link.users || []).map((u) => ({
              ...u,
              link_id: link.link_id,
              link_name: link.link_name,
            }))
          );

          // Group duplicated users by user_id
          // const grouped = Object.values(
          //   merged.reduce((acc, user) => {
          //     if (!acc[user.user_id]) {
          //       acc[user.user_id] = { ...user };
          //     } else {
          //       const prev = new Date(acc[user.user_id].last_active).getTime();
          //       const curr = new Date(user.last_active).getTime();
          //       if (curr > prev) acc[user.user_id] = { ...user };
          //     }
          //     return acc;
          //   }, {})
          // );
          // setActiveUsers(grouped);
          // const grouped = Object.values(
          //   merged.reduce((acc, user) => {
          //     if (!acc[user.user_id]) {
          //       acc[user.user_id] = { ...user };
          //     } else {
          //       const prev = new Date(acc[user.user_id].last_active).getTime();
          //       const curr = new Date(user.last_active).getTime();
          //       if (curr > prev) acc[user.user_id] = { ...user };
          //     }
          //     return acc;
          //   }, {})
          // );
          // // 🔽 sort by login time (latest first)
          // grouped.sort(
          //   (a, b) =>
          //     new Date(b.started_at).getTime() -
          //     new Date(a.started_at).getTime()
          // );
          // setActiveUsers(grouped);
          const grouped = Object.values(
            merged.reduce((acc, user) => {
              if (!acc[user.user_id]) {
                acc[user.user_id] = { ...user };
              } else {
                const prev = new Date(acc[user.user_id].last_active).getTime();
                const curr = new Date(user.last_active).getTime();

                // keep the most recent session — INCLUDING its link
                if (curr > prev) acc[user.user_id] = { ...user };
              }

              return acc;
            }, {})
          );

          // sort by login time
          grouped.sort(
            (a, b) =>
              new Date(b.started_at).getTime() -
              new Date(a.started_at).getTime()
          );

          setActiveUsers(grouped);

          if (grouped.length === 0) setNoUsers(true);
          return;
        }

        // ----------------------------------------
        // 🔥 CASE 2: SPECIFIC LINK SELECTED
        // ----------------------------------------
        const matched = allActiveUsers.find(
          (item) => item.link_id === selectedLinkId
        );
        // const filteredUsers = matched?.users || [];
        // setActiveUsers(filteredUsers);
        const filteredUsers = (matched?.users || []).sort(
          (a, b) =>
            new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
        );

        setActiveUsers(filteredUsers);

        if (filteredUsers.length === 0) setNoUsers(true);

        // Fetch locked items count only for selected link
        fetchLockedCountForLink(selectedLinkId);
      } catch (error) {
        console.error(error);
        setNoUsers(true);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchData();
  }, [selectedLinkId]);

  const fetchActiveUsers = async (linkId) => {
    try {
      setLoadingUsers(true);
      setNoUsers(false);

      const activeUsersFetcherURL = `${API_BASE_URL}/api/auth/session/active-users`;
      const activeUsersFetcherRes = await axios.get(activeUsersFetcherURL);
      const allActiveUsers = activeUsersFetcherRes.data?.data || [];

      const matched = allActiveUsers.find((item) => item?.link_id === linkId);

      const filteredUsers = matched?.users || [];
      setActiveUsers(filteredUsers);

      if (filteredUsers.length === 0) {
        setNoUsers(true);
      }
    } catch (err) {
      console.error(err);
      setNoUsers(true);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchLockedCountForLink = async (linkId) => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/auth/stock/active?linkid=${linkId}`
      );

      setItemsLocked((prev) => ({
        ...prev,
        [linkId]: res.data?.data || [],
      }));
    } catch (err) {
      setItemsLocked((prev) => ({
        ...prev,
        [linkId]: [],
      }));
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day}-${month}-${year} : ${hours}:${minutes}`;
  };

  const getActiveMinutes = (startedAt, lastActive) => {
    const start = new Date(startedAt).getTime();
    const last = new Date(lastActive).getTime();
    const diffMin = Math.floor((last - start) / 60000);
    return diffMin < 0 ? 0 : diffMin;
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "" });
  };

  const handleRefresh = () => {
    if (selectedLinkId) {
      fetchActiveUsers(selectedLinkId);
      fetchLockedCountForLink(selectedLinkId);
    } else {
      // refresh all users
      setSelectedLinkId("");
    }

    setSnackbar({ open: true, message: "Data refreshed ✅" });
  };

  const filteredUsers = useMemo(() => {
    return activeUsers?.filter((user) => {
      const matchSearch =
        user.user_name?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
        user.mobile?.includes(searchTerm);
      const matchTime =
        filterTime === "all"
          ? true
          : filterTime === "<15"
          ? user.activeSince < 15
          : filterTime === "15-30"
          ? user.activeSince >= 15 && user.activeSince <= 30
          : filterTime === ">30"
          ? user.activeSince > 30
          : true;
      return matchSearch && matchTime;
    });
  }, [activeUsers, searchTerm, filterTime]);

  return (
    <Box
      sx={{
        bgcolor: "#394A5C",
        minHeight: "100vh",
        px: { xs: 2, md: 6 },
        py: { xs: 3, md: 6 },
      }}
    >
      {/* ---- TOP BAR ---- */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 3,
              bgcolor: "#FFF",
              p: 0.5,
              cursor: "pointer",
            }}
            onClick={() => navigate(-1)}
          >
            <ArrowBackIosNewRoundedIcon
              sx={{ fontSize: 20, fill: "#42A996" }}
            />
          </Box>

          <Box
            sx={{
              width: 36,
              height: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 3,
              bgcolor: "#FFF",
              p: 0.5,
              cursor: "pointer",
            }}
            onClick={() => navigate("/admin-dash")}
          >
            <HomeRoundedIcon sx={{ fontSize: 20, fill: "#42A996" }} />
          </Box>
        </Box>

        <IconButton
          onClick={handleRefresh}
          sx={{
            color: "#42A996",
            "&:hover": { color: "#3b927f" },
            bgcolor: "#fff",
            borderRadius: 2,
            height: 36,
            width: 36,
          }}
        >
          <RefreshIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </Box>

      {/* ---- Title + Select ---- */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mb: 2,
        }}
      >
        <Typography
          sx={{ color: "#fff", fontSize: { xs: 22, sm: 32 }, fontWeight: 600 }}
        >
          Active Links
        </Typography>

        <Select
          value={selectedLinkId || ""}
          onChange={(e) => setSelectedLinkId(e.target.value)}
          size="small"
          displayEmpty
          renderValue={(selected) => {
            if (selected === "") {
              return "All Users"; // 👈 Show this text when empty
            }
            const selectedItem = activeLinks.find(
              (link) => link.linkId === selected
            );
            return selectedItem ? selectedItem.name : "";
          }}
          sx={{
            bgcolor: "#fff",
            borderRadius: 1,
            minWidth: { xs: "100%", sm: 250 },
          }}
        >
          <MenuItem value="">All Users</MenuItem>

          {activeLinks.map((link) => (
            <MenuItem key={link.linkId} value={link.linkId}>
              {link.name}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* ---- Filters ---- */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          flexWrap: "wrap",
          mb: 3,
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            width: { xs: "100%", md: "70%" },
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <TextField
            placeholder="Search user by name or mobile"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              flex: 1,
              bgcolor: "#fff",
              borderRadius: 1,
              width: "100%",
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#667A8A" }} />
                </InputAdornment>
              ),
            }}
          />

          <Select
            value={filterTime}
            onChange={(e) => setFilterTime(e.target.value)}
            size="small"
            sx={{
              bgcolor: "#fff",
              minWidth: { xs: "100%", sm: 220 },
              borderRadius: 1,
            }}
          >
            <MenuItem value="all">All Users</MenuItem>
            <MenuItem value="<15">Active &lt; 15 min</MenuItem>
            <MenuItem value="15-30">Active 15–30 min</MenuItem>
            <MenuItem value=">30">Active &gt; 30 min</MenuItem>
          </Select>
        </Box>
      </Box>

      {/* ---- Table ---- */}
      <Box
        sx={{
          bgcolor: "#2A3A4B",
          borderRadius: 2,
          overflowX: "auto",
          "& table": { tableLayout: "fixed", width: "100%" },
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "rgba(30,47,64,0.95)" }}>
              {[
                "Name",
                "Mobile",
                "Login Date & Time",
                "Active Since (min)",
                "Last Active",
                "Session Expiry",
                "Stock Items",
              ].map((head, i) => (
                <TableCell
                  key={i}
                  sx={{
                    color: "#fff",
                    fontWeight: 600,
                    width: { xs: "150px", md: "15%" },
                    whiteSpace: "nowrap",
                  }}
                >
                  {head}
                </TableCell>
              ))}
              {selectedLinkId === "" && (
                <TableCell
                  sx={{
                    color: "#fff",
                    fontWeight: 600,
                    width: { xs: "150px", md: "15%" },
                    whiteSpace: "nowrap",
                  }}
                >
                  Link Name
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {loadingUsers && (
              <TableRow>
                <TableCell
                  colSpan={7}
                  sx={{ color: "#fff", textAlign: "center", py: 3 }}
                >
                  Loading user data...
                </TableCell>
              </TableRow>
            )}

            {!loadingUsers && noUsers && (
              <TableRow>
                <TableCell
                  colSpan={7}
                  sx={{ color: "#fff", textAlign: "center", py: 3 }}
                >
                  No active user found
                </TableCell>
              </TableRow>
            )}

            {!loadingUsers &&
              !noUsers &&
              filteredUsers?.map((user, index) => (
                <TableRow
                  onClick={() =>
                    navigate("/user-activity-log", {
                      state: {
                        user,
                        linkId:
                          selectedLinkId === "" ? user.link_id : selectedLinkId,
                      },
                    })
                  }
                  key={user.user_id}
                  sx={{
                    cursor: "pointer",
                    bgcolor: index % 2 === 0 ? "#1E2F40" : "#2A3A4B",
                  }}
                >
                  <TableCell sx={{ color: "#fff" }}>{user.user_name}</TableCell>

                  <TableCell sx={{ color: "#fff" }}>{user.mobile}</TableCell>

                  <TableCell sx={{ color: "#fff" }}>
                    {formatDateTime(user.started_at)}
                  </TableCell>

                  <TableCell sx={{ color: "#fff" }}>
                    {getActiveMinutes(user.started_at, user.last_active)}
                  </TableCell>

                  <TableCell sx={{ color: "#fff" }}>
                    {formatDateTime(user.last_active)}
                  </TableCell>

                  <TableCell sx={{ color: "#fff" }}>
                    {formatDateTime(user.expires_at)}
                  </TableCell>

                  <TableCell sx={{ color: "#fff" }}>
                    {itemsLocked[selectedLinkId]?.filter(
                      (item) => item.user_id === user.user_id
                    )?.length || 0}
                  </TableCell>

                  {selectedLinkId === "" && (
                    <TableCell sx={{ color: "#fff" }}>
                      {user.link_name || "-"}
                    </TableCell>
                  )}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Box>

      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
};

export default ActiveUser;
